import { useSyncExternalStore } from 'react';

/**
 * Darstellung (docs/19): dunkel (Halle/Beamer) oder hell mit hohem Kontrast
 * (draußen in der Sonne, Piazza) — oder **automatisch** nach dem Gerät
 * (`prefers-color-scheme`, mit Horcher: stellt das Handy abends um, folgt die
 * App sofort). Dazu „große Schrift“ für Tablets und ältere Augen. Alles liegt
 * im Browser; die Farben sind CSS-Variablen in index.css
 * (`html[data-theme="light"]`), die Schriftgröße ein `zoom` auf body.
 *
 * `data-theme` trägt immer die **aufgelöste** Darstellung (dark/light), damit
 * das CSS nichts von „auto“ wissen muss; die Wahl selbst steht in
 * `data-theme-wahl`. Neue Geräte starten mit `auto`; wer früher hell oder
 * dunkel gewählt hat, behält das.
 */
export type Theme = 'auto' | 'dark' | 'light';
export type Resolved = 'dark' | 'light';
const THEME_KEY = 'airmail.theme';
const ZOOM_KEY = 'airmail.zoom';

function read<T extends string>(key: string, ok: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v && (ok as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

let theme: Theme = read(THEME_KEY, ['auto', 'dark', 'light'] as const, 'auto');
let large = read(ZOOM_KEY, ['normal', 'large'] as const, 'normal') === 'large';
const listeners = new Set<() => void>();

/** Was das Gerät wünscht — ohne `matchMedia` (alte WebViews, Tests) dunkel */
const mql = typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: light)') : null;
function geraet(): Resolved {
  return mql?.matches ? 'light' : 'dark';
}
export function resolvedTheme(): Resolved {
  return theme === 'auto' ? geraet() : theme;
}

function apply(): void {
  const wirksam = resolvedTheme();
  document.documentElement.dataset.theme = wirksam;
  document.documentElement.dataset.themeWahl = theme;
  document.documentElement.dataset.zoom = large ? 'large' : 'normal';
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = wirksam === 'light' ? '#f4f6f7' : '#0c1114';
}
apply();

function emit(): void {
  apply();
  for (const fn of listeners) fn();
}
// Das Gerät schaltet um (Sonnenuntergang, Systemeinstellung) — nur in „auto“ von Belang
mql?.addEventListener?.('change', () => {
  if (theme === 'auto') emit();
});

export function setTheme(next: Theme): void {
  theme = next;
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* egal */
  }
  emit();
}
export function setLarge(next: boolean): void {
  large = next;
  try {
    localStorage.setItem(ZOOM_KEY, next ? 'large' : 'normal');
  } catch {
    /* egal */
  }
  emit();
}
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
export function useTheme(): { theme: Theme; resolved: Resolved; large: boolean } {
  useSyncExternalStore(subscribe, () => `${theme}|${resolvedTheme()}|${large}`);
  return { theme, resolved: resolvedTheme(), large };
}
